/**
 * Session/guild slice — Phase 2 thin delegate over the host-owned
 * `SessionPort` + `GuildPort`. The state refs and helpers that used to
 * live here (`session`, `sessionLoading`, `activeDiscordGuildId`,
 * `dbGuildId`, `availableGuilds`, `guildsLoading`,
 * `ownerDiscordIdByGuildId`, `loadAvailableGuilds`,
 * `resolveDbGuildId`, `setActiveDiscordGuildId`, `setGuildOfficer`,
 * `isMemberOf` / `isOfficerOf` / `isOwnerOf`) now live under
 * `src/app/{session,guild}.ts` (host-owned) and `@/shared/ports`
 * (declares the contracts).
 *
 * This module's job:
 *
 * 1. Re-export the host-owned refs as `Readonly<Ref<...>>` so every
 *    downstream slice sees the same `Readonly<>` envelope the port
 *    declares. The bound reference is the port's *identity* — there
 *    is no widened local copy — which is the only way the host can
 *    stay the sole writer of `activeDiscordGuildId`. The previous
 *    `as unknown as Ref<string>` cast was the F-05 bypass vector: it
 *    let a slice mutate the ref under a writable type even though the
 *    port declared it read-only. Pinning the cast away at the source
 *    level (`guildChangeRouting.test.ts`) prevents the regression.
 *
 * 2. Re-implement `setActiveDiscordGuildId` to (a) call the port's
 *    `setActive()` (which fires the cascade registry) and (b) run the
 *    legacy cascade inline so Phase 2 keeps the existing on-switch
 *    behaviour (fetchBoard / loadRaids / fetchPlayerTags + WS
 *    resubscribe + clear `liveSyncError`). Phase 7 moves the cascade
 *    to `onActiveGuildChange()` registration inside `installGvgPlanner`.
 *
 * 3. Keep the auto-switch behaviour from `loadAvailableGuilds` ("if
 *    the persisted guild isn't in the list, switch to the first")
 *    here so the cascade work still fires on that switch.
 *
 * Public API preserved (114 keys — see
 * `stores/__tests__/publicApi.snapshot.test.ts`):
 *
 * - state: `activeDiscordGuildId`, `dbGuildId`, `availableGuilds`,
 *   `guildsLoading`
 * - getters: `isMember`, `isOfficer`, `isOwner`,
 *   `activeGuildOwnerDiscordId`
 * - actions: `loadAvailableGuilds`, `resolveDbGuildId`,
 *   `setActiveDiscordGuildId`, `setGuildOfficer`
 *
 * Re-exported for the rest of the store (the composer's
 * `features/gvg-planner/stores/gvgPlannerStore.ts` calls
 * `useSessionState()` and `useSessionActions(ctx)` — those factories
 * stay available as thin shims for the existing wiring).
 */
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'

import { useSession, useGuild } from '@/shared/ports'

import { SetGuildOfficerDocument } from '../lib/handWrittenGraphql'

// Local alias — see `stores/board/actions.ts` for the rationale.
const apolloClient = resolveClient()

function snowflakeVar(s: string): string {
  return s
}

/** Convert the host's string-form DB primary key at the store boundary. */
function parseDbGuildId(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

/**
 * Per-shape session view exposed to the planner store's composer. The
 * refs come from the host port; `isMember` / `isOfficer` / `isOwner`
 * recompute against the host-owned `activeDiscordGuildId`.
 *
 * Every ref is declared as `Readonly<Ref<...>>` to mirror the port's
 * contract and to prevent a slice from mutating the host's ref at the
 * type level. The only legitimate mutation path is `port.setActive(next)`
 * inside `setActiveDiscordGuildId`; the F-05 regression was caused by
 * widening `activeDiscordGuildId` to a bare `Ref<string>` here.
 *
 * `ownerDiscordIdByGuildId` is a `Map<number, string>` matching the
 * existing store API — the port's ReadonlyMap is keyed by the DB id
 * (string), so the slice converts at the boundary.
 */
export interface SessionStateApi {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly dbGuildId: Readonly<Ref<number | null>>
  readonly availableGuilds: Readonly<Ref<
    ReadonlyArray<{
      readonly id: number
      readonly name: string
      readonly canEditHistoricalRaids: boolean
      readonly discordGuildId: string
    }>
  >>
  readonly guildsLoading: Readonly<Ref<boolean>>
  readonly isMember: ComputedRef<boolean>
  readonly isOfficer: ComputedRef<boolean>
  readonly isOwner: ComputedRef<boolean>
  readonly canEditHistoricalRaids: ComputedRef<boolean>
  readonly activeGuildOwnerDiscordId: ComputedRef<string>
  readonly ownerDiscordIdByGuildId: Map<number, string>
}

export function useSessionState(): SessionStateApi {
  const port = useGuild()
  const sessionPort = useSession()

  /**
   * The planner never mutates the host's active-guild ref. Binding the
   * port's identity (no widening cast) keeps the `Readonly<>` wrapper
   * intact at the type level so a future slice cannot reassign the ref
   * through this alias — the only legitimate mutation path is
   * `port.setActive(next)` inside `setActiveDiscordGuildId`.
   */
  const activeDiscordGuildId = port.activeDiscordGuildId

  /**
   * The host port owns a string-form DB primary key while the established
   * planner-store contract exposes a number. Keep the converted ref reactive
   * so every existing consumer, including the officer roster, receives the
   * numeric GraphQL `Int` value it declares.
   */
  const dbGuildId = ref<number | null>(parseDbGuildId(port.dbGuildId.value))
  watch(port.dbGuildId, (value) => {
    dbGuildId.value = parseDbGuildId(value)
  })

  /**
   * Map `GuildSummary[]` (id:number) into the existing
   * `{id,name,discordGuildId}` projection so the public API is unchanged.
   * Bind the port's ref identity directly — the `Readonly<>` wrapper is
   * preserved at the type level, matching the port's contract.
   */
  const availableGuilds = port.availableGuilds as Readonly<Ref<
    ReadonlyArray<{
      readonly id: number
      readonly name: string
      readonly discordGuildId: string
      readonly canEditHistoricalRaids: boolean
    }>
  >>

  const guildsLoading = port.loading

  /**
   * Snapshot of the port's owner-map keyed by the DB id (string), but
   * re-shaped as `Map<number, string>` for back-compat with the legacy
   * composer's read sites (the board stores DB ids as numbers).
   * Recomputed eagerly on read so mutations through `loadAvailableGuilds`
   * see the latest owners.
   */
  const ownerDiscordIdByGuildId = new Map<number, string>()

  // The port ref is `Readonly<Ref<ReadonlyMap<...>>>`. The slice needs a
  // mutable Map-like handle — recompute `ownerDiscordIdByGuildId` from
  // the port's current value on every getter read. The Map's identity
  // stays stable across calls (matches the original `useSessionState`
  // contract: `ownerDiscordIdByGuildId: Map<number, string>`).
  const syncOwnerMap = (): void => {
    const portMap = port.ownerDiscordIdByGuildId.value
    ownerDiscordIdByGuildId.clear()
    for (const [k, v] of portMap.entries()) {
      ownerDiscordIdByGuildId.set(Number(k), v)
    }
  }

  const activeGuild = computed(() =>
    port.availableGuilds.value.find(
      (guild) => guild.discordGuildId === activeDiscordGuildId.value,
    ),
  )
  // `availableGuilds` is the host's server-authoritative `canView`
  // projection. An entry here is the complete read capability: it covers
  // ordinary members whose current access-role snapshot passes and owners
  // (including the configured superadmin), whose valid owner access does not
  // require a raw Discord-membership entry. Rechecking `memberFor` here made
  // a superadmin see the signed-out calendar placeholder for a guild they
  // were already authorized to manage.
  const canViewActiveGuild = computed<boolean>(() => activeGuild.value !== undefined)
  const isMember = computed<boolean>(() => canViewActiveGuild.value)
  /**
   * Owner access is present in both session sources. `/auth/me` is the
   * primary source, while `MyGuilds.ownerDiscordId` is the authoritative
   * active-guild record and covers sessions refreshed before the owner list
   * was persisted. Keeping both makes the edit gate resilient during the
   * session/guild hydration cascade.
   */
  const isOwner = computed<boolean>(() => {
    if (!canViewActiveGuild.value) return false
    const userId = sessionPort.user.value?.discordUserId
    const ownerId = activeGuild.value?.ownerDiscordId
    return sessionPort.isOwnerOf(activeDiscordGuildId.value)
      || (userId !== undefined && ownerId !== undefined && ownerId === userId)
  })
  /** Owners have the same board-edit capability as officers. */
  const isOfficer = computed<boolean>(() =>
    canViewActiveGuild.value
      && (sessionPort.isOfficerOf(activeDiscordGuildId.value) || isOwner.value),
  )
  const canEditHistoricalRaids = computed<boolean>(
    () => activeGuild.value?.canEditHistoricalRaids ?? false,
  )
  const activeGuildOwnerDiscordId = computed<string>(() => {
    const match = availableGuilds.value.find(
      (g) => g.discordGuildId === activeDiscordGuildId.value,
    )
    if (!match) return ''
    syncOwnerMap()
    return ownerDiscordIdByGuildId.get(match.id) ?? ''
  })

  // Suppress unused-var warning while keeping a reference that documents
  // the dependency on the session port. The session port's `user` ref is
  // read indirectly via `sessionPort.isMemberOf` etc.; `sessionPort`
  // keeps the wiring discoverable from a single search.
  void sessionPort

  return {
    activeDiscordGuildId,
    dbGuildId,
    availableGuilds,
    guildsLoading,
    isMember,
    isOfficer,
    isOwner,
    canEditHistoricalRaids,
    activeGuildOwnerDiscordId,
    ownerDiscordIdByGuildId,
  }
}

// ── Session actions ────────────────────────────────────────────────────────

export interface SessionActionsCtx {
  readonly liveSyncError: Ref<string | null>
  readonly error: Ref<string | null>
  readonly subscription: Ref<{ unsubscribe(): void } | null>
}

export interface SessionActionsApi {
  readonly loadAvailableGuilds: () => Promise<void>
  readonly resolveDbGuildId: () => Promise<number | null>
  readonly setActiveDiscordGuildId: (next: string) => Promise<void>
  readonly setGuildOfficer: (discordUserId: string, flag: boolean) => Promise<boolean>
}

export function useSessionActions(ctx: SessionActionsCtx): SessionActionsApi {
  const port = useGuild()

  /** Resolve the active guild's DB id through the port. */
  async function resolveDbGuildId(): Promise<number | null> {
    const id = await port.resolveDbGuildId(port.activeDiscordGuildId.value)
    if (id === null) return null
    const n = Number(id)
    return Number.isFinite(n) ? n : null
  }

  /**
   * Hydrate `availableGuilds` from the port, then run the legacy
   * auto-switch fallback (if the persisted guild isn't in the list and
   * there's at least one available, switch to the first). The fallback
   * calls `setActiveDiscordGuildId` below so the cascade fires — the
   * port's `loadAvailableGuilds` does **not** include the auto-switch
   * (that's Phase 7's responsibility).
   */
  async function loadAvailableGuilds(): Promise<void> {
    await port.loadAvailableGuilds()
    const list = port.availableGuilds.value as ReadonlyArray<{
      readonly id: number
      readonly name: string
      readonly discordGuildId: string
      readonly canEditHistoricalRaids: boolean
    }>
    const keep = list.some((g) => g.discordGuildId === port.activeDiscordGuildId.value)
    if (!keep && list.length > 0) {
      const first = list[0]
      if (first !== undefined) {
        await setActiveDiscordGuildId(first.discordGuildId)
      }
    }
  }

  /**
   * Update the active guild + the WS-error banner. Phase 7 lifts the
   * fetchBoard / loadRaids / fetchPlayerTags cascade out of this
   * function and into the host port's `onActiveGuildChange` registry
   * (registered by `installGvgPlanner` during app boot). The cascade
   * handlers fire when `port.setActive(next)` runs, so callers still
   * see the board + raids + tags re-fetch on every switch — the only
   * difference is that the fetches are now fire-and-forget from the
   * cascade instead of awaited inline. Timing-wise this matches the
   * old behavior (`loadRaids` + `fetchPlayerTags` were already
   * fire-and-forget); `fetchBoard` is no longer awaited here, so
   * callers that awaited this function will resolve marginally sooner
   * on a guild switch.
   *
   * The `liveSyncError` + `subscription` cleanup stays inline: those
   * are store-internal teardowns for the previous-guild WS connection
   * and the global error banner, not part of the cascade.
   */
  async function setActiveDiscordGuildId(next: string): Promise<void> {
    ctx.liveSyncError.value = null
    ctx.subscription.value?.unsubscribe()
    ctx.subscription.value = null
    await port.setActive(next)
  }

  async function setGuildOfficer(discordUserId: string, flag: boolean): Promise<boolean> {
    try {
      const res = await apolloClient.mutate<{ setGuildOfficer: boolean }>({
        mutation: SetGuildOfficerDocument,
        variables: {
          guildId: snowflakeVar(port.activeDiscordGuildId.value),
          discordUserId: snowflakeVar(discordUserId),
          isOfficer: flag,
        },
      })
      const ok = res.data?.setGuildOfficer ?? false
      if (!ok) ctx.error.value = 'Failed to update officer status.'
      return ok
    } catch (e: unknown) {
      ctx.error.value = e instanceof Error ? e.message : String(e)
      return false
    }
  }

  return {
    loadAvailableGuilds,
    resolveDbGuildId,
    setActiveDiscordGuildId,
    setGuildOfficer,
  }
}
